import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ArtworkCard from '@/components/ArtworkCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';

const categories = ['all', 'flowers', 'landscapes', 'fantasy', 'animals', 'abstract'];

const Gallery = () => {
  const { t, lang } = useI18n();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: artworks = [], isLoading } = useQuery({
    queryKey: ['artworks', selectedCategory, searchQuery],
    queryFn: async () => {
      let query = supabase.from('artworks').select('*').order('created_at', { ascending: false });
      if (selectedCategory !== 'all') query = query.eq('category', selectedCategory);
      if (searchQuery) query = query.or(`title.ilike.%${searchQuery}%,title_ar.ilike.%${searchQuery}%`);
      const { data } = await query;
      return data || [];
    },
  });

  const categoryLabels: Record<string, string> = {
    all: t('allCategories'), flowers: t('flowers'), landscapes: t('landscapes'),
    fantasy: t('fantasy'), animals: t('animals'), abstract: t('abstract'),
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Header section removed per user request */}

      {/* Filters */}
      <section className="py-4 border-b-0 sticky top-16 z-40 glass-panel">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-56">
              <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${lang === 'ar' ? 'right-3' : 'left-3'}`} />
              <Input
                placeholder={t('searchArtworks')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={`${lang === 'ar' ? 'pr-10' : 'pl-10'} glass-input h-9 text-sm`}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map(cat => (
                <Button
                  key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={`h-8 text-xs ${selectedCategory === cat
                    ? 'gradient-accent text-primary-foreground'
                    : 'border-border/30 text-muted-foreground hover:text-primary hover:border-primary/30'
                  }`}
                >
                  {categoryLabels[cat]}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="aspect-square bg-muted rounded-xl animate-pulse" />)}
            </div>
          ) : artworks.length === 0 ? (
            <p className="text-center text-muted-foreground py-20">{t('noArtworks')}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {artworks.map(artwork => (
                <ArtworkCard
                  key={artwork.id} id={artwork.id} title={artwork.title}
                  titleAr={artwork.title_ar} price={artwork.price}
                  imageUrl={artwork.image_url} category={artwork.category}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Gallery;
